import app from 'flarum/forum/app';
import { override } from 'flarum/common/extend';
import Notification from 'flarum/forum/components/Notification';
import HandleAvatar from './HandleAvatar';

/**
 * The notification list, read as handles -- Task 5.9a.
 *
 * WHAT BREAKS WITHOUT THIS. Every notification component names its sender by
 * calling `app.translator.trans(key, { user: notification.fromUser() })`, and
 * the translator turns a `user` parameter into a username
 * (`Translator.tsx:78`). With `fromUser` hidden, that call gets `undefined` and
 * `username(undefined)` renders **"[deleted]"** -- so the whole list would read
 * *"[deleted] liked your post"*. The same shape as the replies footer Task 5.5
 * had to rebuild, for the same underlying reason.
 *
 * ONE OVERRIDE, ON `view()`, AND THE CHOICE IS FORCED. The obvious target is
 * `content()`, which is where each component names the sender -- but `content()`
 * is abstract on this class and **implemented on every subclass**, so a patch
 * to `Notification.prototype.content` is shadowed and never runs at all. It
 * would look like a change that did not take effect, which is the worst way for
 * a mistake to present (the same lesson as the `ItemList` keys at 5.5).
 * `view()` is the opposite: no subclass defines one, and it calls `content()`
 * and `excerpt()` itself. Patching it reaches both.
 *
 * NO LIST OF NOTIFICATION TYPES APPEARS BELOW, deliberately. Rewriting each
 * component would mean naming every type this forum has -- across core,
 * likes, mentions and subscriptions -- and silently missing the next one an
 * extension adds. Working at the base class covers the ones we have never
 * heard of, which for a privacy guarantee is the only safe default.
 */
export default function notifications() {
  override(Notification.prototype, 'view', function (original, vnode) {
    const handle = fromHandle(this.attrs.notification);
    if (!handle) {
      /*
       * No sender. Flarum leaves `from_user_id` NULL for system-generated
       * notifications, and the API answers null for those rather than a
       * placeholder -- so there is nobody to rename and core's rendering,
       * which already names nobody, is correct as it stands.
       */
      return original(vnode);
    }
    const rendered = nameSenderByHandle(handle, () => original(vnode));

    /*
     * The avatar, second. `view()` builds its `HeaderListItem` in a single
     * expression, so unlike `CommentPost` at Task 5.6 there is no `avatar()`
     * method to override -- rewriting the one attribute on the returned vnode
     * is the least invasive way in, and leaves the rest of core's view alone.
     */
    if (rendered?.attrs) {
      rendered.attrs.avatar = HandleAvatar(handle);
    }
    return rendered;
  });
}

/**
 * Run `render` with the translator naming `handle` wherever it would have named
 * a user.
 *
 * THE SEAM IS SMALL AND EXACT. `Translator.preprocessParameters()` derives
 * `username` from a `user` parameter **only if `username` is not already set**,
 * so supplying it directly replaces the name without touching the translation
 * key. Every phrasing keeps working, in every language, and nothing here has to
 * know what the sentence says.
 *
 * The swap is undone in a `finally` rather than after the call. A component
 * that throws mid-render would otherwise leave the forum's translator
 * permanently rewritten, and the symptom would appear far from here -- as
 * somebody's real name replaced by a handle in an unrelated part of the UI.
 */
function nameSenderByHandle(handle, render) {
  const translator = app.translator;
  const original = translator.trans;
  translator.trans = function (id, parameters) {
    if (parameters === void 0) {
      parameters = {};
    }
    if (!('user' in parameters)) {
      return original.call(this, id, parameters);
    }

    /*
     * `user` is dropped rather than left beside the `username` that overrides
     * it. Leaving a User model in the parameters of a call already told the
     * name invites the next reader to reach for it -- and by Task 5.5 there is
     * no User there to reach for anyway.
     */
    const {
      user,
      ...rest
    } = parameters;
    void user;
    return original.call(this, id, {
      ...rest,
      username: handle
    });
  };
  try {
    return render();
  } finally {
    translator.trans = original;
  }
}

/**
 * The handle a notification's sender wears in the Issue it is about.
 *
 * Read defensively, for the same reason as `handleOf()`: `fromHandle` is our
 * attribute, and a model that does not carry it must fall through to core's
 * rendering rather than throw inside a render. Null is also the API's honest
 * answer for a notification that has no sender.
 */
export function fromHandle(notification) {
  const candidate = notification;
  if (!candidate || typeof candidate.fromHandle !== 'function') {
    return null;
  }
  const handle = candidate.fromHandle();
  return typeof handle === 'string' && handle.length > 0 ? handle : null;
}